import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { fetchHotels } from "../services/hotelsService";
import { fetchHotelAmenities } from "../services/hotelAmenitiesService";
import { sectionPath } from "../utils/routePaths";
import { resolveSlug } from "../utils/slugify";
import { optimizedImageUrl } from "../utils/cloudinaryUrl";

const ACCENT = "#C47C2B";
// White text on ACCENT is only 3.35:1 (fails WCAG AA's 4.5:1) — this darker
// shade keeps white text readable on solid-fill badges/buttons.
const ACCENT_SOLID = "#996122";

export function HotelCard({ hotel, language, t, onDragStart, amenityTypes = [] }) {
  const [hovered, setHovered] = useState(false);
  const mouseDownX = useRef(0);
  const name = language === "ka" && hotel.nameKa ? hotel.nameKa : hotel.name;
  const location = language === "ka" && hotel.locationKa ? hotel.locationKa : hotel.location;
  const amenities = (hotel.amenities || [])
    .map((id) => amenityTypes.find((a) => a.id === id))
    .filter(Boolean)
    .map((a) => ({ icon: a.icon, en: a.en, ka: a.ka }));
  const roomPrices = (hotel.rooms || []).map((r) => Number(r.price)).filter((n) => !isNaN(n) && n > 0);
  const minPrice = roomPrices.length > 0 ? Math.min(...roomPrices) : null;

  return (
    <Link
      to={sectionPath(language, "hotels", resolveSlug(hotel, language))}
      draggable={false}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseDown={(e) => {
        mouseDownX.current = e.clientX;
        onDragStart?.(e);
      }}
      onClick={(e) => {
        if (Math.abs(e.clientX - mouseDownX.current) > 5) e.preventDefault();
      }}
      className={hotel.featured ? "hotel-card hotel-card-featured" : "hotel-card"}
      style={{
        flexShrink: 0,
        height: 340,
        borderRadius: 20,
        overflow: "hidden",
        position: "relative",
        display: "block",
        background: "#1a1a18",
        textDecoration: "none",
        boxShadow: hovered ? "0 20px 50px rgba(0,0,0,0.45)" : "0 8px 24px rgba(0,0,0,0.3)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        transition: "transform 0.3s, box-shadow 0.3s",
      }}
    >
      <img
        src={hotel.image}
        alt={name}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: hovered ? "scale(1.06)" : "scale(1)",
          transition: "transform 0.5s ease",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to top, rgba(5,8,15,0.95) 0%, rgba(5,8,15,0.55) 45%, rgba(5,8,15,0.1) 100%)",
        }}
      />

      {/* Ad badge */}
      <div
        style={{
          position: "absolute",
          top: 14,
          left: 14,
          background: ACCENT_SOLID,
          color: "#fff",
          borderRadius: 999,
          padding: "5px 12px",
          fontFamily: "Geist, sans-serif",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {t("hotels_ad_badge") || (language === "ka" ? "რეკლამა" : "Ad")}
      </div>

      {/* Content */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "18px 20px" }}>
        <div style={{ marginBottom: 6 }}>
          <h3
            style={{
              fontFamily: "Geist, sans-serif",
              fontSize: hotel.featured ? 21 : 18,
              fontWeight: 700,
              color: "#fff",
              margin: 0,
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
            }}
          >
            {name}
          </h3>
        </div>

        {location && (
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 10 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
              location_on
            </span>
            <span style={{ fontFamily: "Geist, sans-serif", fontSize: 12, color: "rgba(255,255,255,0.65)" }}>
              {location}
            </span>
          </div>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
          {amenities.map((a) => (
            <div key={a.icon} style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14, color: "rgba(255,255,255,0.55)" }}>
                {a.icon}
              </span>
              <span style={{ fontFamily: "Geist, sans-serif", fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
                {language === "ka" ? a.ka : a.en}
              </span>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: minPrice != null ? "space-between" : "flex-end" }}>
          {minPrice != null && (
            <div>
              <span style={{ fontFamily: "Geist, sans-serif", fontSize: 16, fontWeight: 700, color: ACCENT }}>
                ₾{minPrice}
              </span>
              <span style={{ fontFamily: "Geist, sans-serif", fontSize: 12, color: "rgba(255,255,255,0.55)" }}>
                {language === "ka" ? "-დან" : t("hotels_from_short") || "/night"}
              </span>
            </div>
          )}
          <span
            style={{
              background: ACCENT_SOLID,
              color: "#fff",
              border: "none",
              borderRadius: 999,
              padding: "8px 18px",
              fontFamily: "Geist, sans-serif",
              fontSize: 12,
              fontWeight: 700,
              filter: hovered ? "brightness(1.1)" : "brightness(1)",
              transition: "filter 0.2s",
            }}
          >
            {t("hotels_profile_btn") || (language === "ka" ? "პროფილი" : "Profile")}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function HotelsSection() {
  const { language, t } = useLanguage();
  const { hotelsSectionImageUrl } = useSiteSettings();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [amenityTypes, setAmenityTypes] = useState([]);

  useEffect(() => {
    fetchHotels()
      .then((list) => setHotels(list.filter((h) => h.active !== false)))
      .catch((err) => console.error("Error loading hotels:", err))
      .finally(() => setLoading(false));
    fetchHotelAmenities()
      .then((list) => setAmenityTypes(list.map((a) => ({ id: a.id, icon: a.icon, en: a.name, ka: a.nameKa }))))
      .catch((err) => console.error("Error loading hotel amenities:", err));
  }, []);

  const scrollRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollStart = useRef(0);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { updateArrows(); ticking = false; });
    };
    requestAnimationFrame(updateArrows);
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, [updateArrows]);

  const doScrollBy = (dir) => scrollRef.current?.scrollBy({ left: dir * 340, behavior: "smooth" });

  const onDragStart = useCallback((e) => {
    if (e.button !== 0) return;
    isDragging.current = true;
    startX.current = e.clientX;
    scrollStart.current = scrollRef.current?.scrollLeft ?? 0;
    e.preventDefault();
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      if (scrollRef.current) scrollRef.current.scrollLeft = scrollStart.current - (e.clientX - startX.current);
      document.body.style.cursor = "grabbing";
    };
    const onUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const SIDE_PAD = "6vw";

  if (!loading && hotels.length === 0) return null;

  return (
    <section
      style={{ position: "relative", overflow: "hidden", background: "#0a0c12" }}
      className="hotels-section"
    >
      {/* Background photo + premium dark overlay */}
      <img
        src={optimizedImageUrl(hotelsSectionImageUrl, 1920)}
        alt=""
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.5 }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(6,8,14,0.92) 0%, rgba(6,8,14,0.8) 40%, rgba(6,8,14,0.96) 100%)",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          pointerEvents: "none",
        }}
      />

      {/* Content */}
      <div
        style={{ position: "relative", padding: "clamp(48px, 6vw, 88px) 0" }}
      >
        <div
          style={{
            padding: `0 ${SIDE_PAD}`,
            marginBottom: 28,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 15, color: ACCENT }}>
                auto_awesome
              </span>
              <span
                style={{
                  fontFamily: "Geist, sans-serif",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: ACCENT,
                }}
              >
                {t("hotels_eyebrow") || (language === "ka" ? "რჩეული განთავსება" : "Featured Placement")}
              </span>
            </div>
            <h2
              style={{
                fontFamily: "Geist, sans-serif",
                fontSize: "clamp(24px, 3vw, 38px)",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: "#fff",
                margin: "0 0 8px",
              }}
            >
              {t("hotels_title") || (language === "ka" ? "სასტუმროები სვანეთში" : "Hotels in Svaneti")}
            </h2>
            <p style={{ fontFamily: "Geist, sans-serif", fontSize: 14, color: "rgba(255,255,255,0.6)", margin: 0, maxWidth: 440 }}>
              {t("hotels_subtitle") ||
                (language === "ka"
                  ? "აირჩიე კომფორტული ადგილი მთების ხედით"
                  : "Choose a comfortable place with mountain views")}
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className="hotels-arrows" style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => doScrollBy(-1)}
                disabled={!canScrollLeft}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  border: "1.5px solid rgba(255,255,255,0.2)",
                  background: "rgba(255,255,255,0.06)",
                  cursor: canScrollLeft ? "pointer" : "default",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: canScrollLeft ? 1 : 0.3,
                  transition: "opacity 0.2s",
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 17, color: "#fff" }}>
                  arrow_back
                </span>
              </button>
              <button
                onClick={() => doScrollBy(1)}
                disabled={!canScrollRight}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  border: "1.5px solid rgba(255,255,255,0.2)",
                  background: "rgba(255,255,255,0.06)",
                  cursor: canScrollRight ? "pointer" : "default",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: canScrollRight ? 1 : 0.3,
                  transition: "opacity 0.2s",
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 17, color: "#fff" }}>
                  arrow_forward
                </span>
              </button>
            </div>
            <Link
              to={sectionPath(language, "hotels")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: ACCENT_SOLID,
                color: "#fff",
                border: "none",
                padding: "10px 20px",
                borderRadius: 999,
                fontFamily: "Geist, sans-serif",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                whiteSpace: "nowrap",
                textDecoration: "none",
                transition: "filter 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.1)")}
              onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
            >
              {t("hotels_view_all") || (language === "ka" ? "ყველა სასტუმრო" : "All Hotels")}
              <span className="material-symbols-outlined" style={{ fontSize: 15 }}>
                arrow_forward
              </span>
            </Link>
          </div>
        </div>

        <div style={{ paddingLeft: SIDE_PAD, paddingRight: SIDE_PAD }}>
          <div
            ref={scrollRef}
            className="hotels-scroll"
            style={{
              display: "flex",
              gap: 18,
              overflowX: "auto",
              overflowY: "visible",
              scrollbarWidth: "none",
              WebkitOverflowScrolling: "touch",
              cursor: "grab",
              userSelect: "none",
              paddingBottom: 6,
            }}
          >
            {hotels.map((hotel) => (
              <div
                key={hotel.id}
                className={hotel.featured ? "hotels-card-wrap hotels-card-wrap-featured" : "hotels-card-wrap"}
                style={{ width: hotel.featured ? 460 : 280, flexShrink: 0 }}
              >
                <HotelCard
                  hotel={hotel}
                  language={language}
                  t={t}
                  onDragStart={onDragStart}
                  amenityTypes={amenityTypes}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .hotels-scroll::-webkit-scrollbar { display: none; }
        .hotels-scroll > * { scroll-snap-align: start; }
        @media (max-width: 900px) {
          .hotels-arrows { display: none !important; }
        }
        @media (max-width: 640px) {
          .hotels-card-wrap { width: 240px !important; }
          .hotels-card-wrap-featured { width: 300px !important; }
        }
      `}</style>
    </section>
  );
}
